/**
 * Zentrale Date-Utilities für RENDITEFUCHS v2
 *
 * WICHTIG: Alle Funktionen vermeiden Timezone-Konvertierung!
 * Datenbankdaten sind im Format YYYY-MM-DD (ohne Zeit) oder YYYY-MM-DDTHH:MM:SS
 */

/**
 * Parst einen Datums-String (YYYY-MM-DD oder YYYY-MM-DDTHH:MM:SS) zu einem Date-Objekt
 * OHNE Timezone-Konvertierung
 */
export function parseLocalDate(dateString: string): Date {
  const parts = dateString.split('T');
  const dateOnly = parts[0] || dateString; // Remove time part if present
  const dateParts = dateOnly.split('-').map(Number);
  const year = dateParts[0] || 2024;
  const month = dateParts[1] || 1;
  const day = dateParts[2] || 1;
  return new Date(year, month - 1, day);
}

/**
 * ISO-Datum (YYYY-MM-DD) → Date oder undefined.
 * Speziell für <TWDatePicker value={...}> — leere Strings werden korrekt
 * als undefined behandelt (kein 2024-Fallback wie parseLocalDate).
 */
export function isoToDate(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const dateOnly = iso.split('T')[0];
  if (!dateOnly) return undefined;
  const parts = dateOnly.split('-').map((s) => parseInt(s, 10));
  const [y, m, d] = parts;
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

/**
 * Date | undefined → ISO-Datum (YYYY-MM-DD) ohne Timezone-Shift.
 * Speziell für <TWDatePicker onChange={(d) => ...}>.
 */
export function dateToIso(date: Date | undefined | null): string {
  if (!date) return '';
  return formatDateForAPI(date);
}

/**
 * Formatiert ein Date-Objekt zu YYYY-MM-DD (für Backend-API)
 * OHNE Timezone-Konvertierung
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formatiert ein Date-Objekt zu deutschem Datumsformat (z.B. "30.10.2025")
 * OHNE Timezone-Konvertierung
 */
export function formatDateDE(date: Date): string {
  return date.toLocaleDateString('de-DE');
}

/**
 * Formatiert einen Datums-String zu deutschem Format
 * OHNE Timezone-Konvertierung
 */
export function formatDateStringDE(dateString: string): string {
  const date = parseLocalDate(dateString);
  return formatDateDE(date);
}

/**
 * Formatiert einen Datums-String zu langem deutschem Format
 * (z.B. "Do., 30. Okt. 2025")
 * OHNE Timezone-Konvertierung
 */
export function formatDateStringLong(dateString: string): string {
  const parts = dateString.split('T');
  const dateOnly = parts[0] || dateString;
  const dateParts = dateOnly.split('-').map(Number);
  const year = dateParts[0] || 2024;
  const month = dateParts[1] || 1;
  const day = dateParts[2] || 1;
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString('de-DE', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Prüft ob zwei Datums-Strings das gleiche Datum repräsentieren
 * OHNE Timezone-Konvertierung
 */
export function isSameDate(date1: string | Date, date2: string | Date): boolean {
  const str1 = typeof date1 === 'string'
    ? date1.split('T')[0]
    : formatDateForAPI(date1);

  const str2 = typeof date2 === 'string'
    ? date2.split('T')[0]
    : formatDateForAPI(date2);

  return str1 === str2;
}

/**
 * Gibt das heutige Datum als YYYY-MM-DD String zurück
 */
export function getTodayString(): string {
  return formatDateForAPI(new Date());
}

/**
 * Prüft ob ein Datum in der Vergangenheit liegt
 */
export function isPastDate(dateString: string): boolean {
  const date = parseLocalDate(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Prüft ob ein Datum heute ist
 */
export function isToday(dateString: string | Date): boolean {
  const dateStr = typeof dateString === 'string'
    ? dateString.split('T')[0]
    : formatDateForAPI(dateString);

  return dateStr === getTodayString();
}

/**
 * Formatiert ein Date-Objekt zu deutschem Kurzformat mit 2-stelligem Jahr (z.B. "23.12.24")
 */
export function formatDateShortYear(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}.${month}.${year}`;
}

/**
 * Formatiert einen Datums-String zu deutschem Kurzformat mit 2-stelligem Jahr
 */
export function formatDateStringShortYear(dateString: string): string {
  const date = parseLocalDate(dateString);
  return formatDateShortYear(date);
}

/**
 * Formatiert ein Datum relativ zur aktuellen Zeit
 * z.B. "heute", "gestern", "3.1.2025"
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'heute';
  if (diffDays === 1) return 'gestern';

  // Ab 2 Tagen: Echtes Datum anzeigen
  return formatDateStringDE(dateString);
}

/**
 * Formatiert ein Date-Objekt zu langem deutschem Format mit vollem Wochentag
 * z.B. "Montag, 23. Dezember 2024"
 */
export function formatDateLongDE(date: Date): string {
  const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const dayName = days[date.getDay()];
  const day = date.getDate();
  const monthName = months[date.getMonth()];
  const year = date.getFullYear();

  return `${dayName}, ${day}. ${monthName} ${year}`;
}

/**
 * Formatiert einen Datums-String zu langem deutschem Format mit vollem Wochentag
 */
export function formatDateStringLongDE(dateString: string): string {
  const date = parseLocalDate(dateString);
  return formatDateLongDE(date);
}

/**
 * Formatiert ein Date-Objekt mit Datum und Uhrzeit
 * z.B. "23.12.2024, 14:30"
 */
export function formatDateTime(date: Date): string {
  return date.toLocaleString('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

/**
 * Formatiert einen ISO-String mit Datum und Uhrzeit
 */
export function formatDateTimeString(isoString: string): string {
  return new Date(isoString).toLocaleString('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

/**
 * Null-safe Wrapper für formatDateStringDE
 * Gibt Fallback zurück wenn dateString null/undefined ist
 */
export function formatDateStringSafe(dateString: string | null | undefined, fallback: string = '-'): string {
  if (!dateString) return fallback;
  return formatDateStringDE(dateString);
}
